import "@/css/pages/main/social-accounts.scss";
import AvatarWithLogo from "@/js/Components/AvatarWithLogo";
import ConfirmPopup from "@/js/Components/ConfirmPopup";
import InfoPopup from "@/js/Components/InfoPopup";
import Popup, { PopupFooter } from "@/js/Components/Popup";
import SearchField from "@/js/Components/SearchField";
import SocialAccountSettings from "@/js/Components/SocialAccountSettings";
import SocialMediaIcon from "@/js/Components/SocialMediaIcon";
import Status from "@/js/Components/Status";
import Table from "@/js/Components/Table";
import TableActions from "@/js/Components/TableActions";
import Tooltip, { TooltipParent } from "@/js/Components/Tooltip";
import useBreakpoint from "@/js/Hooks/BreakpointHook";
import { useTitle } from "@/js/Layouts/Main";
import { useApp } from "@/js/Providers/AppProvider";
import { useConfirmPopup } from "@/js/Providers/ConfirmPopupProvider";
import { useToaster } from "@/js/Providers/ToasterProvider";
import { useUser } from "@/js/Providers/UserProvider";
import { channelAvatar, initials } from "@/js/common";
import { Button, Checkbox, CheckboxList, RadioGroup, SocialAccountRadio } from "@/js/glidespec";
import { SocialChannelStatus, useCampaigns, useSocialChannels } from "@/js/resources";
import { SocialAccountType } from "@/js/types";
import plans from "@/json/plans.json";
import CheckIcon from "@/svg/check-regular.svg?react";
import FacebookIcon from "@/svg/facebook-logo.svg?react";
import LinkedInIcon from "@/svg/linkedin-logo.svg?react";
import RepeatIcon from "@/svg/repeat-duotone.svg?react";
import SquareShareNodesDuotoneIcon from "@/svg/square-share-nodes-duotone.svg?react";
import SquareShareNodesSolidIcon from "@/svg/square-share-nodes-solid.svg?react";
import TikTokIcon from "@/svg/tiktok-logo.svg?react";
import YouTubeIcon from "@/svg/youtube-logo.svg?react";
import { createGlideChoice } from "@enymo/glide";
import { Form, SubmitHandler } from "@enymo/react-form-component";
import { Column, Row } from "@enymo/react-layout";
import { assertNotNull, requireNotNull } from "@enymo/ts-nullsafe";
import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import route from "ziggy-js";

const errorTypes = ["inUse"] as const;
type ErrorType = typeof errorTypes[number];

interface ConnectionLinkSubmit {
    types: Exclude<SocialAccountType,"instagram">[],
    campaigns: number[]
}

function isError(input: string): input is ErrorType {
    return errorTypes.includes(input as ErrorType);
}

function SocialAccountLabel({type}: {
    type: SocialAccountType
}) {
    const {t} = useTranslation();

    return (
        <div className="account-label">
            <SocialMediaIcon type={type} />
            <span>{t(`social.${type}`)}</span>
        </div>
    )
}

function SocialAccountStatus({status, type, hAlign}: {
    status: SocialChannelStatus,
    type: SocialAccountType,
    hAlign: "left" | "right"
}) {
    const {t} = useTranslation();

    return (
        <Column gap="4px" hAlign={hAlign}>
            {status === "ok" ? (
                <Status color="green">{t("socialAccounts.table.status.ok")}</Status>
            ) : <>
                {status === "will_expire" ? (
                    <Status color="yellow">{t("socialAccounts.table.status.willExpire")}</Status>
                ) : (
                    <Status color="red">{t("socialAccounts.table.status.expired")}</Status>
                )}
                
                <a href={getRoute(type)} className="link renew">
                    <RepeatIcon />
                    <span>{t("socialAccounts.table.renew")}</span>
                </a>
            </>}
        </Column>
    )
}

function getRoute(type: SocialAccountType) {
    switch (type) {
        case "youtube":
            return route("oauth.google", {action: "connect"});
        case "tiktok":
            return route("oauth.tiktok");
        case "facebook":
            return route("oauth.facebook", {action: "connect", type: "user"});
        default:
            return route(`oauth.${type}`, {action: "connect"});
    }
}

const SocialAccountChoice = createGlideChoice({
    indicator: {
        childrenPosition: "right",
        style: {
            width: "26px",
            height: "26px",
            border: "1px solid var(--neutral-200)",
            borderRadius: "8px",
            background: "var(--bg-100)"
        },
        selectedStyle: {
            borderColor: "var(--primary-500)",
            background: "var(--primary-500)"
        },
        element: <CheckIcon width="18px" fill="white" />
    },
    childrenVerticalAlignment: "center",
    wrapperStyle: {
        borderRadius: "8px",
        border: "2px solid var(--neutral-200)",
        padding: "6px 20px",
        cursor: "pointer"
    },
    childrenGap: "30px",
    errorGap: "30px",
    element: ({icon, children}: {
        icon: React.ReactNode,
        children: string
    }) => (
        <Row gap="12px" vAlign="center" flex={1}>
            {icon}
            <span>{children}</span>
        </Row>
    )
})

export default function SocialAccounts() {
    const {t} = useTranslation();
    const {user} = useUser();
    assertNotNull(user, "User must be logged in");
    const withPopup = useConfirmPopup();
    const toast = useToaster();
    const [campaigns] = useCampaigns();
    const [search, setSearch] = useState("");
    const [showAddPopup, setShowAddPopup] = useState(false);
    const [showDeletePopup, setShowDeletePopup] = useState<number | null>(null);
    const [showSettingsPopup, setShowSettingsPopup] = useState<number | null>(null);
    const [showConnectionPopup, setShowConnectionPopup] = useState(false);
    const [connectionLink, setConnectionLink] = useState<string | null>(null);
    const [selectedAddSocial, setSelectedAddSocial] = useState<SocialAccountType>();
    const [searchParams, setSearchParams] = useSearchParams();
    const [socialChannels, {destroy, refresh}] = useSocialChannels();
    const [error, setError] = useState<ErrorType | null>(null);
    const canAddAccounts = useMemo(() => plans[user.subscription_tier].social_accounts + user.additional_social_accounts - socialChannels.length, [user, socialChannels]);
    const filteredSocialChannels = useMemo(() => socialChannels.filter(({name}) => name.toLowerCase().includes(search.toLowerCase())), [socialChannels, search])
    const mobile = useBreakpoint(1100);
    const connectionLinkForm = useForm<ConnectionLinkSubmit>();
    const { axios } = useApp();
    
    useEffect(() => {
        const settings = searchParams.get("settings");
        const error = searchParams.get("error");
        if (settings !== null) {
            setShowSettingsPopup(Number(settings));
        }
        else if (error !== null) {
            if (isError(error)) {
                setError(error);
            }
        }
        else {
            return;
        }
        setSearchParams({}, {replace: true});
    }, [searchParams, setSearchParams, setShowSettingsPopup, setError]);

    const connectRoute = useMemo(() => {
        if (selectedAddSocial === undefined) {
            return "#";
        }
        else {
            return getRoute(selectedAddSocial);
        }
    }, [selectedAddSocial]);
    
    useTitle(t("socialAccounts"));

    const handleClosePopup = () => {
        setShowAddPopup(false);
        setSelectedAddSocial(undefined);
    }

    const handleCloseSettings = (shouldRefresh: boolean) => {
        setShowSettingsPopup(null);
        if (shouldRefresh) {
            refresh();
        }
    }

    const handleDeleteSocialAccount = () => {
        destroy(requireNotNull(showDeletePopup));
        setShowDeletePopup(null);
    }

    const handleGenerateLink: SubmitHandler<ConnectionLinkSubmit> = async data => {
        const response = await axios.post<{
            sid: string
        }>(route("social-accounts.connector-links.generate"), data);

        setConnectionLink(`${window.location.protocol}//${import.meta.env.VITE_CONNECTOR_DOMAIN}/${response.data.sid}`);

        handleCloseConnectionPopup();
    }

    const handleCloseConnectionPopup = () => {
        setShowConnectionPopup(false);
        connectionLinkForm.reset();
    }

    const handleCopyLink = async () => {
        await navigator.clipboard.writeText(connectionLink!);
        toast({
            type: "success",
            duration: 3000,
            title: t("socialAccounts.connectionLink.copy.success.title"),
            text: t("socialAccounts.connectionLink.copy.success.text")
        });
    }

    return (
        <Column gap="30px" className="social-accounts">
            <Row align="space" padding={mobile ? "25px 17px 0 17px" : "60px 50px 0 50px"}>
                <div className="flex gap-7 items-center">
                    <TooltipParent>
                        <Button variant="primary" disabled={canAddAccounts <= 0 && !user.admin} onClick={() => setShowAddPopup(true)}>{mobile ? t("socialAccounts.add.mobile") : t("socialAccounts.add")}</Button>
                        {canAddAccounts <= 0 && !user.admin && <Tooltip>{t("planExceeded.owner")}</Tooltip>}
                    </TooltipParent>
                    <button type="button" onClick={() => setShowConnectionPopup(true)} className="flex items-center gap-1.5 body-m-md text-primary-500">
                        <SquareShareNodesSolidIcon className="w-4.5 fill-primary-500" />
                        {t("socialAccounts.connectionLink.generate")}
                    </button>
                </div>
                <SearchField maxWidth={mobile ? "200px" : "320px"} placeholder={mobile ? t("search") : t("socialAccounts.search")} value={search} onChange={setSearch} />
            </Row>
            <Column padding={mobile ? "0 0 60px 0" : "0 50px 60px 50px"}>
                <Table
                    striped
                    mobileBreakpoint={1100}
                    head={[
                        {
                            label: t("socialAccounts.table.account"),
                            fillWidth: true
                        },
                        ...(mobile ? [] : [{
                            label: t("socialAccounts.table.platform")
                        }, {
                            label: t("socialAccounts.table.status")
                        }]),
                        {
                            label: t("socialAccounts.table.actions")
                        }
                    ]}
                    rows={filteredSocialChannels.map(({
                        id,
                        name,
                        has_avatar,
                        icon_type,
                        account: {
                            type: account_type,
                            status
                        }
                    }) => ({
                        id,
                        data: [
                            <Row gap="20px" vAlign="center">
                                {mobile ? <>
                                    <AvatarWithLogo type={icon_type} size={48} src={channelAvatar({id, has_avatar})} initials={initials(name)} />
                                    <Column gap="5px">
                                        <span className="name">{name}</span>
                                        <SocialAccountStatus status={status} hAlign="left" type={account_type} />
                                    </Column>
                                </> : <>
                                    <AvatarWithLogo 
                                        type={icon_type}
                                        size={48}
                                        src={channelAvatar({id, has_avatar})} 
                                        initials={initials(name)}
                                    />
                                    <span className="name">{name}</span>
                                </>}
                            </Row>,
                            ...(mobile ? [] : [
                                <SocialAccountLabel type={icon_type} />,
                                <SocialAccountStatus status={status} hAlign="right" type={account_type} />
                            ]), 
                            <TableActions delete={{onClick: () => setShowDeletePopup(id), icon: "ban"}} />
                        ]
                    }))}
                    paddingVertical="8px"
                    placeholder={search ? t("socialAccounts.table.placeholder.search") : t("socialAccounts.table.placeholder")}
                />
            </Column>
            {showAddPopup && (
                <Popup onBackgroundClick={handleClosePopup} maxWidth="500px" width="100%">
                    <Column padding="30px 25px" gap="30px">
                        <Row gap="15px" vAlign="center">
                            <SquareShareNodesDuotoneIcon className="primary-500" width="35px" style={{ flexShrink: 0 }} />
                            <h1>{t("socialAccounts.add.title")}</h1>
                        </Row>
                        <Column gap="20px">
                            <p>{t("socialAccounts.add.text")}</p>
                            <RadioGroup value={selectedAddSocial} onChange={setSelectedAddSocial} handlesError gap="12px">
                                <SocialAccountRadio disabled={!user.admin && !plans[user.subscription_tier].facebook} icon={FacebookIcon} title={t("socialAccounts.facebook")} subtitle={!mobile && t("socialAccounts.facebook.description")} value="facebook" />
                                <SocialAccountRadio disabled={!user.admin && !plans[user.subscription_tier].youtube} icon={YouTubeIcon} title={t("socialAccounts.youtube")} subtitle={!mobile && t("socialAccounts.youtube.description")} value="youtube" />
                                <SocialAccountRadio disabled={!user.admin && !plans[user.subscription_tier].tiktok} icon={TikTokIcon} title={t("socialAccounts.tiktok")} subtitle={!mobile && t("socialAccounts.tiktok.description")} value="tiktok" />
                                <SocialAccountRadio disabled={!user.admin && !plans[user.subscription_tier].linkedin} icon={LinkedInIcon} title={t("socialAccounts.linkedin")} subtitle={!mobile && t("socialAccounts.linkedin.description")} value="linkedin" />
                            </RadioGroup>
                        </Column>
                    </Column>
                    <PopupFooter>
                        <Button variant="secondary" onClick={handleClosePopup}>{t("cancel")}</Button>
                        <Button 
                            variant="primary" 
                            disabled={selectedAddSocial === undefined} 
                            to={connectRoute}
                            linkType="no-router"
                        >{t("continue")}</Button>
                    </PopupFooter>
                </Popup>
            )}
            {showDeletePopup !== null && (
                <ConfirmPopup
                    variant="warning"
                    confirmText={t("socialAccounts.delete.button")}
                    title={t("socialAccounts.delete.title")}
                    onConfirm={handleDeleteSocialAccount}
                    onCancel={() => setShowDeletePopup(null)}
                >{t("socialAccounts.delete.text")}</ConfirmPopup>
            )}
            {showSettingsPopup !== null && (
                <Popup onBackgroundClick={() => handleCloseSettings(false)} maxWidth="500px" width="100%">
                    <SocialAccountSettings activationLimit={canAddAccounts} socialAccountId={showSettingsPopup} onClose={handleCloseSettings} />    
                </Popup>
            )}
            {showConnectionPopup && (
                <Popup onBackgroundClick={handleCloseConnectionPopup} maxWidth="500px">
                    <Form form={connectionLinkForm} onSubmit={handleGenerateLink}>
                        <div className="flex flex-col gap-7.5 px-6 py-8">
                            <h1 className="heading-l flex items-center gap-4">
                                <SquareShareNodesDuotoneIcon className="w-9 fill-primary-500" />
                                {t("socialAccounts.connectionLink.generate")}
                            </h1>
                            <div className="flex flex-col gap-4.5">
                                <p className="body-m">{t("socialAccounts.connectionLink.platforms")}</p>
                                <CheckboxList name="types" handlesError gap="12px" options={{
                                    required: t("socialAccounts.connectionLink.platforms.required")
                                }}>
                                    <SocialAccountChoice value="facebook" icon={<FacebookIcon className="size-7.5" />}>{t("social.facebook.business")}</SocialAccountChoice>
                                    <SocialAccountChoice value="youtube" icon={<YouTubeIcon className="size-7.5" />}>{t("social.youtube")}</SocialAccountChoice>
                                    <SocialAccountChoice value="tiktok" icon={<TikTokIcon className="size-7.5" />}>{t("social.tiktok")}</SocialAccountChoice>
                                    <SocialAccountChoice value="linkedin" icon={<LinkedInIcon className="size-7.5" />}>{t("social.linkedin")}</SocialAccountChoice>
                                </CheckboxList>
                            </div>
                            <div className="flex flex-col gap-6">
                                <p className="body-m">{t("socialAccounts.connectionLink.campaigns")}</p>
                                <div className="flex flex-col gap-2 overflow-y-auto max-h-32">
                                    <CheckboxList name="campaigns">
                                        {campaigns.map(({id, name}) => (
                                            <Checkbox key={id} value={id}>{name}</Checkbox>
                                        ))}
                                    </CheckboxList>
                                </div>
                            </div>
                        </div>
                        <PopupFooter align="end" gap="35px">
                            <Button variant="secondary" onClick={() => setShowConnectionPopup(false)}>{t("cancel")}</Button>
                            <Button variant="primary" submit>{t("socialAccounts.connectionLink.submit")}</Button>
                        </PopupFooter>
                    </Form>
                </Popup>
            )}
            {connectionLink !== null && (
                <Popup>
                    <div className="flex flex-col gap-10 px-6 pt-8 pb-15">
                        <div className="flex items-center gap-4">
                            <SquareShareNodesDuotoneIcon className="size-9 fill-primary-500" />
                            <h1 className="heading-l">{t("socialAccounts.connectionLink.copy.title")}</h1>
                        </div>
                        <div className="flex flex-col gap-2">
                            <label className="body-m-md">{t("socialAccounts.connectionLink")}</label>
                            <div className="h-10 px-4 flex items-center rounded-xl border-2 border-neutral-200 body-m">
                                {connectionLink}
                            </div>
                        </div>
                    </div>
                    <PopupFooter align="end" vAlign="center" gap="36px">
                        <Button variant="secondary" onClick={() => setConnectionLink(null)}>{t("close")}</Button>
                        <Button variant="primary" onClick={handleCopyLink}>{t("socialAccounts.connectionLink.copy.button")}</Button>
                    </PopupFooter>
                </Popup>
            )}
            {error !== null && (
                <InfoPopup
                    title={t(`socialAccounts.error.${error}.title`)}
                    onClose={() => setError(null)}
                    variant="warning"
                    confirmText={t("close")}
                >{t(`socialAccounts.error.${error}.text`)}</InfoPopup>
            )}
        </Column>
    )    
}